import {Plugins, Component, EvalEnv, Expressions} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {createPageInitialContext} from './QueryContext';
import {
  DataAdapter,
  DataPaths,
  ExpandedQueryPath,
  PluginArguments,
  QueryContext as QueryContextType,
  QueryLevels,
  QueryPath,
  QueryVariables,
} from '../types';
import {singleMemo, weakMemo} from '@packages/utilities/memo';
import {createAdapter} from './DataAdapter';
import {MutableQualifiedMap, Qualified} from '@packages/systems/wfdl-Qualified';
import {
  analyzeComponents,
  analyzeElement,
  getDataPathsAndQueryLevels,
  type ComponentAnalyzer,
  type ElementAnalysis,
} from './analyzers';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {isUserComponentInst} from '../utils/isUserComponentInst';
import {buildComponentInstancesGetter} from '@packages/systems/user-components/utils';
import {hasSlotDefaultContent} from '@packages/systems/component-slots';

type TreeAnalysis = {
  dataPaths: DataPaths;
  queryLevels: QueryLevels;
};

type AnalyzeTree = (
  skipComponentAnalysis: boolean
) => (
  plugins: Plugins.PluginsType
) => (
  componentMap: Component.ComponentMap
) => (
  componentType: Plugins.QualifiedElement
) => (
  element: ExpressionElement
) => (initialQueryContext: QueryContextType) => TreeAnalysis;

const analyzeTree: AnalyzeTree = singleMemo((skipComponentAnalysis) =>
  weakMemo((plugins) =>
    weakMemo((componentMap) => {
      const getComponentInstances = buildComponentInstancesGetter(componentMap);

      return weakMemo((componentType) =>
        weakMemo((element) =>
          weakMemo((initialQueryContext) => {
            const dataPathsAndQueryLevelsBuilder =
              new DataPathsAndQueryLevelsBuilder(componentMap);

            const {absoluteQueryPaths, dataPathSubTrees} =
              analyzeElement(plugins)(initialQueryContext)(element);
            dataPathsAndQueryLevelsBuilder.ingestComponentAnalysis(
              componentType,
              initialQueryContext.instanceKey,
              dataPathSubTrees,
              absoluteQueryPaths
            );

            if (!skipComponentAnalysis) {
              const componentInstances = isUserComponentInst(element)
                ? [element]
                : getComponentInstances(Expressions.getElementData(element));
              analyzeComponents(
                dataPathsAndQueryLevelsBuilder,
                plugins,
                componentMap,
                initialQueryContext,
                componentInstances
              );
            }

            return {
              dataPaths: dataPathsAndQueryLevelsBuilder.dataPaths,
              queryLevels: dataPathsAndQueryLevelsBuilder.queryLevels,
            };
          })
        )
      );
    })
  )
);

const CONTENT_ANALYZED = Symbol('Content Analyzed');
const COMPLETE = Symbol('Analysis Complete');

class DataPathsAndQueryLevelsBuilder implements ComponentAnalyzer {
  dataPaths: MutableQualifiedMap<
    Component.ComponentName,
    {[instKey: string]: {[elId: string]: ExpandedQueryPath}}
  >;
  queryLevels: [QueryPath[], QueryPath[]];
  componentStatus: MutableQualifiedMap<
    Component.ComponentName,
    typeof CONTENT_ANALYZED | typeof COMPLETE
  >;

  constructor(private readonly componentMap: Component.ComponentMap) {
    this.dataPaths = new MutableQualifiedMap();
    this.componentStatus = new MutableQualifiedMap();
    this.queryLevels = [[], []];
  }

  ingestComponentAnalysis(
    componentType: Qualified<Component.ComponentName>,
    instanceKey: string,
    dataPathSubTrees: ElementAnalysis['dataPathSubTrees'],
    absoluteQueryPaths: ElementAnalysis['absoluteQueryPaths']
  ) {
    const {
      dataPaths: componentInstanceDataPaths,
      queryLevels: [queryLevel0, queryLevel1],
    } = getDataPathsAndQueryLevels(dataPathSubTrees, absoluteQueryPaths);

    let componentDataPaths = this.dataPaths.get(componentType);
    if (componentDataPaths === undefined) {
      componentDataPaths = {};
      this.dataPaths.set_mut(componentType, componentDataPaths);
    }

    componentDataPaths[instanceKey] = componentInstanceDataPaths;

    this.queryLevels[0].push(...queryLevel0);
    this.queryLevels[1].push(...queryLevel1);

    if (queryLevel0.length > 0 || queryLevel1.length > 0) return;

    // Another instance may use defaults that this instance overrides.
    // Marking the component type analyzed would skip that content.
    const component = this.componentMap.get(componentType);
    if (component && hasSlotDefaultContent(component)) {
      return;
    }
    this.componentStatus.set_mut(componentType, CONTENT_ANALYZED);
  }

  updateComponentStatus(
    componentType: Qualified<Component.ComponentName>,
    nestedComponentInstances: ExpressionElement[]
  ) {
    if (this.componentStatus.get(componentType) === CONTENT_ANALYZED) {
      const areNestedComponentsFullyAnalyzed = !nestedComponentInstances.some(
        (inst) => this.componentStatus.get(inst.type) !== COMPLETE
      );
      if (areNestedComponentsFullyAnalyzed) {
        this.componentStatus.set_mut(componentType, COMPLETE);
      }
    }
  }

  shouldAnalyzeComponent(componentType: Qualified<Component.ComponentName>) {
    return this.componentStatus.get(componentType) !== COMPLETE;
  }

  shouldAnalyzeContent(componentType: Qualified<Component.ComponentName>) {
    return !this.componentStatus.has(componentType);
  }
}

export function getQueryLevels(
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  componentType: Plugins.QualifiedElement,
  plugins: Plugins.PluginsType,
  page: PageRecordType,
  variables: QueryVariables,
  pluginArguments: PluginArguments,
  evalEnv: EvalEnv.EvalEnvType,
  baseRendererNativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[],
  rootComponentType: Qualified<Component.ComponentName>
): {
  adapter: DataAdapter;
  queryLevels: QueryLevels;
} {
  // TODO (platform): Use empty initial query context if element is in a Symbol
  // See: https://webflow.atlassian.net/browse/PLAT-1315
  const initialContext = createPageInitialContext(
    element,
    page,
    variables,
    pluginArguments,
    evalEnv,
    baseRendererNativeIdPath,
    focusedSymbolNativeIdPath,
    rootComponentType,
    componentMap,
    plugins
  );

  // Prevent expanding components in designer query generation
  // so that we don't end up with duplicate queries for collection lists
  const skipComponentAnalysis =
    pluginArguments.runtimeEnvironment === WebflowEnvironments.DESIGNER;
  const {dataPaths, queryLevels} = analyzeTree(skipComponentAnalysis)(plugins)(
    componentMap
  )(componentType)(element)(initialContext);

  const adapter = createAdapter(pluginArguments, componentMap, dataPaths);

  return {adapter, queryLevels};
}
